/**
 * Shell shared by every Trailblazers statistics card.
 *
 * Owns the one thing all the cards must agree on: how a statistic that was
 * never RECORDED is shown. A game played before the event ledger (migration
 * 048) has no scanner, clue or disruption-attempt rows, and rendering that as
 * "0" would assert something false — "nobody armed a scanner" and "we weren't
 * counting yet" are different claims. `availability` turns the card's body into
 * an explicit notice instead.
 */

import { ReactNode } from 'react';
import type { TrlStatAvailability } from '@swtorbit/shared-socket-events';

export interface StatCardProps {
  title: string;
  icon?: string;
  /** Short right-aligned summary in the header (e.g. "69 landed"). */
  summary?: string;
  /**
   * When 'not-recorded', the body is replaced by a notice. Omit for cards built
   * from always-present tables (captures, sabotage, treasure).
   */
  availability?: TrlStatAvailability;
  /** What the notice should say this game predates. */
  notRecordedLabel?: string;
  /** Rendered when there is genuinely nothing to show, but recording was on. */
  emptyMessage?: string;
  isEmpty?: boolean;
  children: ReactNode;
}

export function StatCard({
  title,
  icon,
  summary,
  availability,
  notRecordedLabel = 'This statistic',
  emptyMessage = 'Nothing recorded.',
  isEmpty = false,
  children,
}: StatCardProps) {
  const notRecorded = availability === 'not-recorded';

  return (
    <div className="bg-space bg-o-50 border-md border-blue-light rounded-base p-sm d-flex flex-column gap-xs">
      <div className="d-flex justify-content-between align-items-center gap-xs flex-wrap">
        <h4 className="text-cyan">
          {icon && <span className="me-2xs">{icon}</span>}
          {title}
        </h4>
        {summary && !notRecorded && <span className="fs-sm text-white">{summary}</span>}
      </div>

      {notRecorded ? (
        <span className="fs-sm text-slate">
          {notRecordedLabel} was not being recorded when this game was played, so there is no data —
          this is not a zero. Games played from now on will show it.
        </span>
      ) : isEmpty ? (
        <span className="fs-sm text-cyan">{emptyMessage}</span>
      ) : (
        children
      )}
    </div>
  );
}
